import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { BookOpen, Users, Layers, ArrowRight } from 'lucide-react';

export default function AcademicSetup() {
    const navigate = useNavigate();
    const { subjects, sections, mappings, faculty } = useData();

    const stats = [
        {
            title: 'Subjects',
            count: subjects.length,
            icon: BookOpen,
            color: 'bg-blue-500',
            link: '/dashboard/subjects',
            description: 'Manage course catalog & codes'
        },
        {
            title: 'Sections',
            count: sections.length,
            icon: Users,
            color: 'bg-emerald-500',
            link: '/dashboard/sections',
            description: 'Configure class sections & roll ranges'
        },
        {
            title: 'Faculty Mappings',
            count: mappings.length,
            icon: Layers,
            color: 'bg-purple-500',
            link: '/dashboard/mappings',
            description: 'Assign faculty to subjects & sections'
        }
    ];

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-3xl font-bold font-heading text-primary">Academic Setup</h1>
                <p className="text-muted-foreground mt-1">
                    Manage the core academic structure, from subjects to faculty assignments.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stats.map((stat, idx) => (
                    <div
                        key={idx}
                        onClick={() => navigate(stat.link)}
                        className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer group relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
                            <stat.icon size={100} />
                        </div>

                        <div className="relative z-10 flex flex-col h-full">
                            <div className={`h-12 w-12 rounded-lg ${stat.color} bg-opacity-10 flex items-center justify-center mb-4`}>
                                <stat.icon className={`text-white`} size={24} />
                            </div>

                            <h3 className="text-xl font-bold text-foreground mb-1">{stat.title}</h3>
                            <p className="text-sm text-muted-foreground mb-6">{stat.description}</p>

                            <div className="mt-auto flex items-end justify-between">
                                <div>
                                    <span className="text-3xl font-bold text-foreground">{stat.count}</span>
                                    <span className="text-sm text-muted-foreground ml-2">Total</span>
                                </div>
                                <div className={`h-8 w-8 rounded-full ${stat.color} flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
                                    <ArrowRight size={16} className="text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Background Overlay on Hover */}
                        <div className={`absolute inset-0 ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                    </div>
                ))}
            </div>

            {/* Quick Tips or Recent Activity could go here */}
            <div className="bg-secondary/20 border border-border rounded-xl p-6">
                <h3 className="font-bold text-lg mb-2">Getting Started</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Create <strong>Subjects</strong> that are offered in your department.</li>
                    <li>Define <strong>Sections</strong> (Classes) for each year and set their roll number ranges.</li>
                    <li>Use <strong>Faculty Mappings</strong> to link specific Faculty members to Subjects and Sections.</li>
                    <li>Once configured, these mappings will be available for Timetable creation and Attendance marking.</li>
                </ol>
            </div>
        </div>
    );
}
